import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Issue, BlockerEntry, IssueStatus, DependencyLinkType } from '../types';
import { STATUS_LABELS } from '../types';
import { dependencyLinks, wouldCycle, dateConflict, type DepCard } from '../dependencies';
import type { WorkCalendar } from '../work-calendar';
import Collapsible from './Collapsible';

/**
 * DEPENDS ON / BLOCKS — the card's finish-to-start (and SS/FF/SF) dependencies on
 * other cards, split out from "What's blocking this" (Vinnie, 3 Nov 2026) so a
 * card link reads as SEQUENCING, not as a red blocker. It is the same data — the
 * card-kind entries of `blocker_refs` — surfaced on its own, with the MS-Project
 * link type (FS/SS/FF/SF) and a working-day lag now first-class on each link.
 *
 * SOFT BY DEFAULT. A predecessor that isn't ready shows a calm, dismissible note;
 * the card can still be started. Only on a board where an admin has turned on
 * `depHardBlock` does an open predecessor become a true block.
 *
 * It owns no persistence: it takes the card's dependency entries and emits the
 * next list; the modal holds the working copy and writes `blocker_refs` on save.
 * Each dependency is written as its own single-card entry, so a row maps 1:1 to a
 * ref — but a legacy entry that named several cards still renders a row each.
 */

const LINK_TYPES: DependencyLinkType[] = ['FS', 'SS', 'FF', 'SF'];

/** The card as the dependency core reads it. */
const asDep = (i: Issue | { id?: string; ref: string; status: IssueStatus; startsOn?: string | null; dueDate?: string | null; blockerRefs?: BlockerEntry[] }): DepCard => ({
  id: (i as Issue).id ?? i.ref,
  ref: i.ref,
  status: i.status,
  startsOn: i.startsOn,
  dueDate: i.dueDate,
  blockerRefs: (i as { blockerRefs?: BlockerEntry[] }).blockerRefs,
});

const isResolved = (s: IssueStatus) => s === 'done' || s === 'wontdo';
const hasStarted = (s: IssueStatus) => s !== 'backlog' && s !== 'todo';

/** "+2d" / "−1d" / "" — the signed working-day lag, MS-Project shorthand. */
function lagLabel(lag: number): string {
  if (!lag) return '';
  return `${lag > 0 ? '+' : '−'}${Math.abs(lag)}d`;
}

/** "28 Oct 2026" for a cleared predecessor's finish. */
function fmtDay(ms?: number | null, iso?: string | null): string {
  const d = ms ? new Date(ms) : iso ? new Date(`${iso}T00:00:00`) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The plain-language twin for a link — never a formula (copy rule). The subject is
 *  the predecessor named in the row above, so it is left implicit. */
function plainTwin(type: DependencyLinkType, lag: number): string {
  const n = Math.abs(lag);
  const d = n === 1 ? '1 working day' : `${n} working days`;
  switch (type) {
    case 'FS':
      return lag > 0 ? `Finishes before this starts, then ${d} before this can begin.`
        : lag < 0 ? `This can begin ${d} before it finishes.`
          : 'Finishes before this starts.';
    case 'SS':
      return lag > 0 ? `This can begin ${d} after it starts.`
        : lag < 0 ? `This can begin ${d} before it starts.`
          : 'This can begin once it has started.';
    case 'FF':
      return lag > 0 ? `This can't finish until ${d} after it finishes.`
        : lag < 0 ? `This can finish ${d} before it finishes.`
          : "This can't finish until it finishes.";
    case 'SF':
      return lag > 0 ? `This can't finish until ${d} after it starts.`
        : lag < 0 ? `This can finish ${d} before it starts.`
          : "This can't finish until it starts.";
  }
}

/** The editor's live preview sentence — the predecessor is named in bold before it. */
function previewSentence(type: DependencyLinkType, lag: number, enforced: boolean): string {
  const n = Math.abs(lag);
  const d = n === 1 ? '1 working day' : `${n} working days`;
  let core: string;
  switch (type) {
    case 'FS': core = lag > 0 ? `must finish, then this waits ${d} before it starts` : lag < 0 ? `must finish; this may begin ${d} before that` : 'must finish before this starts'; break;
    case 'SS': core = lag > 0 ? `must start, then this begins ${d} after` : lag < 0 ? `must start; this may begin ${d} before that` : 'must start before this begins'; break;
    case 'FF': core = lag > 0 ? `must finish, then this finishes ${d} after` : lag < 0 ? `must finish; this may finish ${d} before that` : 'must finish before this finishes'; break;
    case 'SF': core = lag > 0 ? `must start, then this finishes ${d} after` : lag < 0 ? `must start; this may finish ${d} before that` : 'must start before this finishes'; break;
  }
  const holiday = lag !== 0 ? ' Holidays and weekends are skipped.' : '';
  // Only FS/SS gate the START, so only they become a hard block when a board enforces
  // dependencies; FF/SF constrain the finish, which is never start-gated — promising a
  // hard block there would be a lie (the start is never held).
  const startGated = type === 'FS' || type === 'SS';
  const tail = enforced
    ? startGated
      ? 'On this board that start is held as a hard block.'
      : 'This board enforces dependencies, but a finish link only guides the finish — it never holds the start.'
    : "It's a guide, not a lock, unless an admin enforces it.";
  return `${core}.${holiday} ${tail}`;
}

/** How a SUCCESSOR waits on this card, read from its link type — so an SS/FF/SF
 *  successor isn't mislabelled "starts after this" (which is only true for FS). */
function successorPhrase(type: DependencyLinkType): string {
  switch (type) {
    case 'FS': return 'starts after this';
    case 'SS': return 'starts after this starts';
    case 'FF': return 'finishes after this';
    case 'SF': return 'finishes after this starts';
  }
}

/** The relationship hint under the type picker — names the current pick in words. */
function typeHint(type: DependencyLinkType, predRef: string): string {
  const r = predRef || 'it';
  switch (type) {
    case 'FS': return `Finish → Start (the usual): ${r} finishes before this starts.`;
    case 'SS': return `Start → Start: this can begin once ${r} has started.`;
    case 'FF': return `Finish → Finish: this can't finish until ${r} finishes.`;
    case 'SF': return `Start → Finish: this can't finish until ${r} starts.`;
  }
}

interface Row { ei: number; ri: number; ref: string; type: DependencyLinkType; lag: number; label?: string; }

interface Props {
  /** The card's dependency entries (card-kind blockers), the modal's working copy. */
  entries: BlockerEntry[];
  onChange: (next: BlockerEntry[]) => void;
  /** Every card on the board — live status for each predecessor, and the successors. */
  allIssues: Issue[];
  /** This card, as the graph reads it. */
  self: { id?: string; ref: string; status: IssueStatus; startsOn?: string | null; dueDate?: string | null };
  /** The current user's address, stamped on a new link so the notify trigger can name them. */
  me: string;
  /** Whether this board enforces dependencies as a hard block (admin setting). */
  enforced: boolean;
  /** The board's working calendar, so the date-clash check skips the right days. */
  workCalendar?: WorkCalendar;
  /** Move this card's start to the suggested date (clears the clash in one click). */
  onMoveStart?: (iso: string) => void;
  /** Read-only view (shown, not editable). */
  readOnly?: boolean;
}

/** What the card modal can call at save-time — mirrors BlockerSection's handle. */
export interface DependencySectionHandle {
  /** Fold a half-built dependency in, returning the list to write, or null when
   *  nothing is pending (or the pending one isn't yet committable). */
  flushPending: () => BlockerEntry[] | null;
}

const DependencySection = forwardRef<DependencySectionHandle, Props>(function DependencySection({ entries, onChange, allIssues, self, me, enforced, workCalendar, onMoveStart, readOnly }, ref) {
  const byRef = useMemo(() => new Map(allIssues.map((i) => [i.ref, i] as const)), [allIssues]);

  // The predecessor rows, flattened from the card-kind entries (one row per card ref).
  const rows: Row[] = [];
  entries.forEach((e, ei) => e.refs.forEach((r, ri) => {
    if (r.kind !== 'card' || !r.ref || r.ref === self.ref) return;
    rows.push({ ei, ri, ref: r.ref, type: r.linkType ?? 'FS', lag: r.lag ?? 0, label: r.label });
  }));

  // The successors — cards on this board that name THIS card as a predecessor.
  const successors = useMemo(
    () => allIssues.filter((i) => i.ref !== self.ref && dependencyLinks(asDep(i)).some((l) => l.ref === self.ref)),
    [allIssues, self.ref],
  );

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // The editor: closed, adding, or editing an existing row (its ei/ri).
  const [editor, setEditor] = useState<null | { at: { ei: number; ri: number } | 'new'; ref: string; label?: string; query: string; type: DependencyLinkType; lag: number }>(null);

  const linkedRefs = new Set(rows.map((r) => r.ref));

  // THE DATE CLASH (soft) — this card's own start sits before a start-constraining
  // predecessor (FS/SS) needs it. Names the furthest-out one and the first start that
  // clears it, so the note can offer a one-click move.
  //   · Off in read-only (nothing to move).
  //   · Off when the board ENFORCES dependencies — there the hard block already says
  //     "can't start yet", and the clash note's "you can leave it — they can overlap"
  //     would flatly contradict it on the same predecessor.
  //   · Off once THIS card is resolved (done/wontdo) — a finished card can't clash.
  const selfResolved = isResolved(self.status);
  const clash = useMemo(
    () => (readOnly || enforced || selfResolved ? null : dateConflict(asDep({ ...self, blockerRefs: entries }), new Map(allIssues.map((i) => [i.ref, asDep(i)] as const)), workCalendar)),
    [readOnly, enforced, selfResolved, self, entries, allIssues, workCalendar],
  );
  // The clash note glides out when it clears, so it keeps rendering for a beat after
  // `clash` goes null — hold the last value so those frames don't deref null.
  const lastClash = useRef(clash);
  if (clash) lastClash.current = clash;
  const clashView = clash ?? lastClash.current;
  const clashType = clashView ? rows.find((r) => r.ref === clashView.predecessorRef)?.type ?? 'FS' : 'FS';

  function writeRows(next: BlockerEntry[]) { onChange(next); }

  function removeRow(row: Row) {
    const next = entries
      .map((e, ei) => ei !== row.ei ? e : { ...e, refs: e.refs.filter((_, ri) => ri !== row.ri) })
      // A legacy entry can predate the `text` field (card links written before it
      // existed carry none), so read it defensively — dropping its last ref must
      // not throw on `undefined.trim()`.
      .filter((e) => e.refs.length > 0 || (e.text ?? '').trim() !== '');
    writeRows(next);
  }

  function openAdd() { setEditor({ at: 'new', ref: '', query: '', type: 'FS', lag: 0 }); }
  function openEdit(row: Row) {
    // Fill the field the same way picking a card does — "#142 Provision…", not a bare
    // "#142" — so Edit and Add read identically for the same card.
    setEditor({ at: { ei: row.ei, ri: row.ri }, ref: row.ref, label: row.label, query: row.label ? `${row.ref} ${row.label}` : row.ref, type: row.type, lag: row.lag });
  }

  // The next entry list a committable editor would produce — shared by the button,
  // the Enter key and the save-time flush, so all three fold the draft in identically.
  type OpenEditor = NonNullable<typeof editor>;
  function nextFromEditor(ed: OpenEditor): BlockerEntry[] {
    // The label is optional: omit the key entirely when it is absent, rather than
    // send an undefined value, which the database layer rejects (test-wiring).
    const label = ed.label ? { label: ed.label } : {};
    if (ed.at === 'new') {
      const entry: BlockerEntry = { type: 'item', text: '', refs: [{ kind: 'card', ref: ed.ref, ...label, linkType: ed.type, lag: ed.lag }], at: Date.now(), by: me };
      return [...entries, entry];
    }
    const { ei, ri } = ed.at;
    return entries.map((e, i) => i !== ei ? e : {
      ...e,
      refs: e.refs.map((r, j) => j !== ri ? r : { ...r, ref: ed.ref, ...label, linkType: ed.type, lag: ed.lag }),
    });
  }
  function commitEditor() {
    if (!editor || !editor.ref || wouldCycle(allIssues, self.ref, editor.ref)) return;
    writeRows(nextFromEditor(editor));
    setEditor(null);
  }

  // Save-time flush — a card picked but not yet "Add dependency"-ed is written
  // rather than lost, the same courtesy the blocker editor already gets. A draft
  // with no card chosen, or one that would close a cycle, is NOT committable, so
  // it returns null (nothing pending to fold in).
  useImperativeHandle(
    ref,
    () => ({
      flushPending() {
        if (!editor || !editor.ref || wouldCycle(allIssues, self.ref, editor.ref)) return null;
        const next = nextFromEditor(editor);
        onChange(next);
        setEditor(null);
        return next;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, entries, me, allIssues, self.ref],
  );

  // Typeahead candidates — board cards matching the query, never this card, never
  // one already linked, never one that would close a cycle.
  const candidates = useMemo(() => {
    if (!editor) return [] as Issue[];
    const q = editor.query.trim().toLowerCase();
    const editingRef = editor.at !== 'new' ? editor.ref : null;
    return allIssues
      .filter((i) => i.ref !== self.ref)
      .filter((i) => i.ref === editingRef || !linkedRefs.has(i.ref))
      .filter((i) => !q || i.ref.toLowerCase().includes(q) || (i.title ?? '').toLowerCase().includes(q))
      .filter((i) => !wouldCycle(allIssues, self.ref, i.ref))
      .slice(0, 6);
  }, [editor, allIssues, self.ref]);

  // The editor glides out when it closes, so it keeps rendering for a beat after
  // `editor` goes null — hold the last value so those frames don't deref null. While
  // the editor is live, editorView === editor, so interaction is unchanged.
  const lastEditor = useRef(editor);
  if (editor) lastEditor.current = editor;
  const editorView = editor ?? lastEditor.current;

  // A chosen predecessor that already waits on this card would close a cycle — refuse
  // it at link time (the date-clash itself is surfaced by `clash` above, soft).
  const editorCycle = editorView?.ref ? wouldCycle(allIssues, self.ref, editorView.ref) : false;

  const count = rows.length;
  if (readOnly && count === 0 && successors.length === 0) return null;

  return (
    <div className="dep-sec">
      <div className="dep-h">
        <span className="lbl">Depends on</span>
        {count > 0 && <span className="dep-count">{count}</span>}
        <span className="dep-mode">
          <svg className="ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></svg>
          {enforced ? 'Enforced on this board' : 'Sequence only — not a hard block'}
        </span>
      </div>

      {count > 0 && (
        <div className="dep-list">
          {rows.map((row) => {
            const pred = byRef.get(row.ref);
            const cleared = pred ? isResolved(pred.status) : false;
            // Does this link hold the start? FS wants the predecessor finished, SS
            // wants it started; FF/SF constrain the finish, not the start. A card that
            // is itself resolved (done/wontdo) is past starting, so it shows no nag.
            const open = pred != null && !cleared && !selfResolved && (
              (row.type === 'FS' && !isResolved(pred.status)) ||
              (row.type === 'SS' && !hasStarted(pred.status))
            );
            const key = `${row.ei}:${row.ri}`;
            return (
              <div key={key} className={`dep-row${cleared ? ' is-cleared' : ''}`}>
                <div className="dep-row-top">
                  <span className="dep-dot" style={{ background: pred ? `var(--s-${pred.status})` : 'var(--t3)' }} aria-hidden="true" />
                  <span className="dep-ref">{row.ref}</span>
                  <span className="dep-ti">{pred?.title ?? row.label ?? 'Not on this board'}</span>
                  {pred && <span className="dep-state" style={{ color: `var(--s-${pred.status}-ink, var(--t2))` }}>{STATUS_LABELS[pred.status]}</span>}
                  <button type="button" className="dep-link" onClick={() => !readOnly && openEdit(row)} title={readOnly ? undefined : 'Change the link type or lag'} disabled={readOnly}>
                    {row.type}{row.lag !== 0 && <span className="lag">{lagLabel(row.lag)}</span>}
                  </button>
                  {!readOnly && (
                    <span className="dep-tools">
                      <button type="button" className="dep-tool" aria-label="Change this dependency" onClick={() => openEdit(row)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                      </button>
                      <button type="button" className="dep-tool" aria-label="Remove this dependency" onClick={() => removeRow(row)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    </span>
                  )}
                </div>

                {cleared ? (
                  <div className="dep-plain" style={{ color: 'var(--s-done-ink)' }}>
                    Cleared{pred?.completedAt || pred?.dueDate ? ` — finished ${fmtDay(pred?.completedAt, pred?.dueDate)}` : ''}, so it no longer holds this up.
                  </div>
                ) : (
                  <div className="dep-plain">{plainTwin(row.type, row.lag)}</div>
                )}

                <Collapsible show={open && enforced} className="dep-hard">
                  <svg className="ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                  <span>Can't start yet — <span style={{ fontWeight: 700 }}>{row.ref} must {row.type === 'SS' ? 'be under way' : 'finish'} first</span>. <span className="sub">Dependencies are enforced on this board.</span></span>
                </Collapsible>
                <Collapsible show={open && !enforced && !dismissed.has(key) && !(clash != null && clash.predecessorRef === row.ref && !dismissed.has('clash'))} className="dep-soft">
                  <svg className="ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
                  <span><b>{row.ref} {row.type === 'SS' ? "hasn't started yet." : "isn't finished yet."}</b> You can still start this now — the two can run together if you need them to.</span>
                  {!readOnly && <button type="button" className="x" onClick={() => setDismissed((s) => new Set(s).add(key))}>Dismiss</button>}
                </Collapsible>
              </div>
            );
          })}
        </div>
      )}

      <Collapsible show={clash != null && !dismissed.has('clash')} className="dep-clash">
        {clashView && (
          <>
            <svg className="ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>
            <span>
              This is set to start <b>{fmtDay(null, self.startsOn)}</b>, before <b>{clashView.predecessorRef}</b> {clashType === 'SS' ? 'starts' : 'finishes'}. You can leave it — they can overlap — or line it up.
            </span>
            {onMoveStart && (
              <button type="button" className="move" onClick={() => onMoveStart(clashView.suggestedStart)}>Move to {fmtDay(null, clashView.suggestedStart)}</button>
            )}
            <button type="button" className="x" onClick={() => setDismissed((s) => new Set(s).add('clash'))}>Dismiss</button>
          </>
        )}
      </Collapsible>

      {successors.length > 0 && (
        <>
          <div className="dep-grouplbl">Blocks — {successors.length} card{successors.length === 1 ? '' : 's'} wait{successors.length === 1 ? 's' : ''} on this</div>
          <div className="dep-succ">
            {successors.map((s) => {
              const link = dependencyLinks(asDep(s)).find((l) => l.ref === self.ref);
              return (
                <span key={s.ref} className="brc">
                  <span className="brc-dot" style={{ background: `var(--s-${s.status})` }} aria-hidden="true" />
                  <span className="brc-ref">{s.ref}</span>
                  <span className="brc-ti">{s.title}</span>
                  <span className="brc-state">{STATUS_LABELS[s.status]} · {successorPhrase(link?.type ?? 'FS')}</span>
                </span>
              );
            })}
          </div>
        </>
      )}

      {!readOnly && (
        <Collapsible show={!editor}>
          <button type="button" className="dep-add" onClick={openAdd}>
            <svg className="ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            Add a dependency
          </button>
        </Collapsible>
      )}

      {!readOnly && (
        <Collapsible show={editor != null}>
          {editorView && (
            <div
              className="dep-editor"
              // Escape backs out of just this sub-editor — and stops there, so it never
              // bubbles up to close the whole card modal with a half-typed dependency.
              onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setEditor(null); } }}
              style={editorCycle ? { borderColor: 'color-mix(in srgb, var(--p-critical) 45%, var(--line))' } : undefined}
            >
              <div className="write-field">
                <span className="lbl">Which card has to finish first <span className="req">*</span></span>
                <div className="ta-wrap">
                  <input
                    className="ctl"
                    autoFocus
                    value={editorView.query}
                    placeholder="Type a #number or a title"
                    onChange={(e) => setEditor({ ...editorView, query: e.target.value, ref: '', label: '' })}
                    // Enter picks the first match, then (once a card is chosen) commits —
                    // the add flow completes from the keyboard alone.
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      if (!editorView.ref && candidates.length > 0) {
                        const c = candidates[0];
                        setEditor({ ...editorView, ref: c.ref, label: c.title, query: `${c.ref} ${c.title}` });
                      } else if (editorView.ref && !editorCycle) {
                        commitEditor();
                      }
                    }}
                  />
                  {editorView.query.trim() !== '' && !editorView.ref && candidates.length > 0 && (
                    <div className="ta-menu">
                      <div className="ta-head">Cards on this board</div>
                      {candidates.map((c) => (
                        <button type="button" key={c.ref} className="ta-row" onClick={() => setEditor({ ...editorView, ref: c.ref, label: c.title, query: `${c.ref} ${c.title}` })}>
                          <span className="brc-dot" style={{ background: `var(--s-${c.status})` }} aria-hidden="true" />
                          <span>{c.ref} {c.title}</span>
                          <span className="sub">{STATUS_LABELS[c.status]}</span>
                        </button>
                      ))}
                      <div className="ta-tip">Pick the card this one waits on</div>
                    </div>
                  )}
                  {editorView.query.trim() !== '' && !editorView.ref && candidates.length === 0 && (
                    <div className="ta-menu">
                      <div className="ta-empty">No card matches — it may already be linked, or it already waits on this one.</div>
                    </div>
                  )}
                </div>
              </div>

              {editorCycle ? (
                <p className="ble-err">
                  <svg className="ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                  <span>That would make the two cards wait on each other. {editorView.ref} already waits on this one, so it can't also come first.</span>
                </p>
              ) : (
                <>
                  <div className="dep-twocol">
                    <div className="write-field">
                      <span className="lbl">Relationship</span>
                      <div className="seg">
                        {LINK_TYPES.map((t) => (
                          <button type="button" key={t} className={`seg-btn${editorView.type === t ? ' active' : ''}`} aria-pressed={editorView.type === t} onClick={() => setEditor({ ...editorView, type: t })}>{t}</button>
                        ))}
                      </div>
                      <span className="hint">{typeHint(editorView.type, editorView.ref || 'the other card')}</span>
                    </div>
                    <div className="write-field">
                      <span className="lbl">Lag / lead</span>
                      <div className="lagstep">
                        <button type="button" aria-label="Less lag" onClick={() => setEditor({ ...editorView, lag: editorView.lag - 1 })}>−</button>
                        <span className="v">{editorView.lag > 0 ? `+${editorView.lag}` : editorView.lag < 0 ? `−${Math.abs(editorView.lag)}` : '0'}<small>working days</small></span>
                        <button type="button" aria-label="More lag" onClick={() => setEditor({ ...editorView, lag: editorView.lag + 1 })}>+</button>
                      </div>
                    </div>
                  </div>

                  <div className="dep-preview">
                    <svg className="ic" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                    <span>
                      <b>{editorView.label ? `${editorView.label} (${editorView.ref})` : editorView.ref || 'The card you pick'}</b>{' '}
                      {previewSentence(editorView.type, editorView.lag, enforced)}
                    </span>
                  </div>
                </>
              )}

              <div className="dep-foot">
                <button type="button" className="btn btn-primary btn-small" disabled={!editorView.ref || editorCycle} onClick={commitEditor}>
                  {editorView.at === 'new' ? 'Add dependency' : 'Save'}
                </button>
                <button type="button" className="ble-cancel" onClick={() => setEditor(null)}>Cancel</button>
              </div>
            </div>
          )}
        </Collapsible>
      )}
    </div>
  );
});

export default DependencySection;
